import type { BrowserOptions, ErrorEvent, EventHint } from '@sentry/angular';
import { reloadOnceIfStaleChunk } from './chunk-reload';
import { scrubBreadcrumb, scrubEvent } from './sentry-scrub';

/**
 * The web `Sentry.init` options, out of `main.ts` so a test can build a real
 * client from exactly what production uses (FR-059 U8 A1-G1: the scrub wiring was
 * untested — `main.ts` bootstraps the app on import, so nothing could read its
 * options). `main.ts` calls `Sentry.init(sentryInitOptions(environment))`.
 */
export interface SentryWebEnv {
  readonly SENTRY_DSN_WEB: string;
  readonly SENTRY_ENV: string;
  readonly SENTRY_RELEASE: string;
}

/**
 * `beforeSend`: the drops (automation, self-healed stale chunk, injected code),
 * then the PII scrub.
 */
export function sentryBeforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  // #680: blanket-drop every event originating from an automated browser
  // session (Playwright e2e harness, Selenium, headless smoke runs). Such runs
  // set navigator.webdriver === true; a real user's browser leaves it
  // false/undefined. This runs BEFORE the injected-frame heuristic below so a
  // genuine app-bundle error that an e2e flow happens to surface is dropped too
  // — the harness is not a real user and must never page us. SSR / non-browser
  // contexts have no `navigator`, hence the typeof guard.
  if (typeof navigator !== 'undefined' && navigator.webdriver === true) {
    return null;
  }
  // Stale-chunk self-heal on the global path. In this zoneless app a failed lazy
  // import after a redeploy reaches Sentry as an unhandledrejection/onerror,
  // bypassing the Angular ErrorHandler + router hooks that already handle the
  // in-Angular paths (#302/#311). Reload once to fetch the new bundle and drop the
  // self-healed event; if the cooldown guard suppressed the reload (the chunk is
  // genuinely gone, not merely stale) fall through and report it so a broken deploy
  // still surfaces. (#302 recurrence — TIVEDO-WEB-7/8)
  if (reloadOnceIfStaleChunk(hint?.originalException)) {
    return null;
  }
  // Drop errors thrown by injected / eval'd in-page code that isn't ours: the
  // Playwright e2e harness's page.evaluate snippets + accessibility-snapshot
  // script, plus any DevTools console snippet / bookmarklet. Such frames carry
  // no fetched-script URL (abs_path null / `<anonymous>` / function `eval`),
  // whereas a real app or dependency bundle error ALWAYS has an
  // https://…/main-xxxx.js frame — even when sourcemaps fail to resolve. So drop
  // only when the event has frames and NONE come from a real script; this keeps
  // genuine app errors (including ones an e2e run legitimately surfaces) while
  // shedding injected noise. window.onerror reports the latter against the page.
  const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
  if (
    frames.length > 0 &&
    !frames.some((f) => /^https?:\/\//.test(f.abs_path ?? f.filename ?? ''))
  ) {
    return null;
  }
  // Drop email/name/ip if anything sets them, and mask every email address in
  // the exception, message, extra, contexts, tags and breadcrumbs (FR-059 AC-4.2;
  // parity with the API's start/sentry.ts).
  return scrubEvent(event);
}

export function sentryInitOptions(env: SentryWebEnv): BrowserOptions {
  return {
    dsn: env.SENTRY_DSN_WEB,
    environment: env.SENTRY_ENV,
    release: env.SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    // Deliberately NOT using enableLogs + consoleLoggingIntegration: that combo
    // ships console.* to Sentry's Logs product via beforeSendLog, which bypasses
    // the beforeSend PII scrub below — a stray console.log({ user }) would leak
    // email/name unscrubbed (defeats AC-4). The SDK's default BreadcrumbsIntegration
    // already captures console.* as breadcrumbs, attached only to error events and
    // therefore scrubbed via beforeSend. SENTRY_LOG_LEVEL no longer gates the
    // frontend console-breadcrumb level (it still drives the API breadcrumb stream).
    beforeSend: sentryBeforeSend,
    // FR-059 AC-4.2 — mask at record time too: message, URL-valued data (url,
    // navigation from/to; secret query values filtered) and console breadcrumbs'
    // data.arguments.
    beforeBreadcrumb: scrubBreadcrumb,
  };
}
